import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Toolbar, Search, Controls, List, Row, Empty, Loading, humanize } from "@/components/calm/Calm";

// Fine-grained booking statuses grouped into the few views that matter.
const GROUPS = [
  { key: 'todo', label: 'To do', statuses: ['paid', 'new'] },
  { key: 'active', label: 'In progress', statuses: ['contacted', 'booked'] },
  { key: 'closed', label: 'Closed', statuses: ['closed'] },
  { key: 'all', label: 'All' },
];

const NEXT_STEP = {
  paid: 'Schedule the shoot',
  new: 'Reply to the lead',
  contacted: 'Waiting to hear back',
  booked: 'Shoot booked',
  closed: 'Closed',
};

const EMPTY = {
  todo: 'Nothing to do. New and paid bookings show up here.',
  active: 'No bookings in progress.',
  closed: 'No closed bookings yet.',
  all: 'No bookings yet. Paid shoots from the booking page land here.',
};

function fmtQuote(amount) {
  if (amount == null || amount === '') return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function inGroup(b, key) {
  const g = GROUPS.find(x => x.key === key);
  return !g || !g.statuses || g.statuses.includes(b.status || '');
}

export default function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('todo');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/bookings', { params: { status: 'all' } })
      .then(r => { setBookings(r.data || []); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        const status = err?.response?.status;
        toast.error(status === 401 || status === 403
          ? 'Sign in as platform owner to see bookings'
          : 'Could not load bookings');
      });
  };
  useEffect(() => { load(); }, []);

  const filtered = bookings.filter(b => {
    const hay = [
      b.agent_name, b.email, b.phone, b.brokerage,
      b.property_address, b.package_interest,
    ].filter(Boolean).join(' ').toLowerCase();
    return (!search || hay.includes(search.toLowerCase())) && inGroup(b, tab);
  });

  const todo = bookings.filter(b => inGroup(b, 'todo')).length;
  const booked = bookings.filter(b => b.status === 'booked').length;

  if (loading) return <Loading />;

  return (
    <Page testid="bookings-page">
      <PageHeader title="Bookings" subtitle="Paid shoots from the booking page" />

      <Stats items={[
        { label: 'To do', value: todo, attention: todo > 0 },
        { label: 'Booked', value: booked },
        { label: 'All leads', value: bookings.length },
      ]} />

      <Controls>
        <StatusTabs options={GROUPS} value={tab} onChange={setTab} testid="bookings-status-filter" />
        <Toolbar>
          <Search value={search} onChange={setSearch} placeholder="Search name, email or address" />
        </Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty>{search ? `No bookings match "${search}".` : EMPTY[tab]}</Empty>
      ) : (
        <List testid="bookings-list">
          {filtered.map(b => {
            const quote = fmtQuote(b.quote_total);
            const attention = b.status === 'paid' || b.status === 'new';
            return (
              <Row
                key={b.id}
                testid="booking-row"
                title={b.agent_name || b.email || 'Unnamed lead'}
                meta={[b.property_address, b.package_interest || humanize(b.property_type)].filter(Boolean)}
                next={[
                  { text: NEXT_STEP[b.status] || 'Review', attention },
                  b.email && b.agent_name ? { text: b.email } : null,
                  b.phone ? { text: b.phone, figure: true } : null,
                  quote ? { text: quote, figure: true } : null,
                  b.created_at ? { text: fmtDay(b.created_at), figure: true } : null,
                ].filter(Boolean)}
                status={humanize(b.status) || 'Unknown'}
                attention={attention}
              />
            );
          })}
        </List>
      )}
    </Page>
  );
}
